const POSITION_FIELDS = [
  'startMeasure', 'startBeat', 'startSubdivision',
  'endMeasure', 'endBeat', 'endSubdivision'
];

/**
 * Service for handling annotation management operations (create, update, delete)
 */
export class AnnotationManagementService {
  constructor(annotationDataService) {
    this.dataService = annotationDataService;
  }

  /**
   * Update song general information
   */
  updateSongGeneralInfo(uiGeneralInfo) {
    const annotationSheet = this.dataService.getAnnotationSheet();

    if (!annotationSheet) {
      console.warn('Annotation sheet not initialized.');
      return false;
    }

    if (!annotationSheet.general_info) {
      annotationSheet.general_info = {};
    }

    const gi = annotationSheet.general_info;
    gi.original_filename = uiGeneralInfo.original_filename;
    gi.song_title = uiGeneralInfo.song_title;
    gi.artist = uiGeneralInfo.artist;
    gi.lyricist = uiGeneralInfo.lyricist;
    gi.songwriter = uiGeneralInfo.songwriter;
    gi.arranger = uiGeneralInfo.arranger;
    gi.composer = uiGeneralInfo.composer;
    gi.year_of_publication = uiGeneralInfo.year_of_publication;
    gi.presence_of_main_melody = uiGeneralInfo.presence_of_main_melody;
    gi.music_type = uiGeneralInfo.music_type;
    gi.duration.number_of_bars = uiGeneralInfo.duration_number_of_bars;
    gi.duration.time_length = uiGeneralInfo.duration_time_length;
    gi.music_completeness = uiGeneralInfo.music_completeness;
    gi.music_compositional_craftsmanship = uiGeneralInfo.music_compositional_craftsmanship;
    gi.emotion = uiGeneralInfo.emotion;
    gi.imagery = uiGeneralInfo.imagery;
    gi.regional_classification = uiGeneralInfo.regional_classification;
    gi.music_style = uiGeneralInfo.music_style;
    gi.music_style_description = uiGeneralInfo.music_style_description;
    gi.professional_description = uiGeneralInfo.professional_description;
    gi.casual_description = uiGeneralInfo.casual_description;
    gi.completed = uiGeneralInfo.completed;
    this.dataService.notifyListeners();
    return true;
  }

  /**
   * Create a new session-local annotation block.
   */
  createAnnotation(uiAnnotation, options) {
    if (!this.dataService.annotationSheet) return false;

    const created = this.dataService.annotationBlockStore.createBlock(uiAnnotation, options);
    if (!created) return false;
    this.dataService.notifyListeners();
    return created;
  }

  createAnnotations(uiAnnotations, options) {
    if (!this.dataService.annotationSheet) {
      return { success: false, reason: 'sheet-not-initialized' };
    }

    const result = this.dataService.annotationBlockStore.createBlocks(uiAnnotations, options);
    if (!result.success) return result;
    this.dataService.notifyListeners();
    return result;
  }

  /**
   * Update an existing annotation by session ID. The three-argument form remains
   * compatible with existing callers, but the stale full annotation is ignored.
   */
  updateAnnotation(id, uiAnnotationOrChanges, maybeChanges, options) {
    if (!this.dataService.annotationSheet) return false;

    const changes = maybeChanges === undefined ? uiAnnotationOrChanges : maybeChanges;
    if (!changes || !this.dataService.annotationBlockStore.getBlock(id)) return false;

    const positionPatch = Object.fromEntries(
      POSITION_FIELDS
        .filter(field => Object.prototype.hasOwnProperty.call(changes, field) && changes[field] !== undefined)
        .map(field => [field, changes[field]])
    );
    const hasPositionPatch = Object.keys(positionPatch).length > 0;
    const hasFormPatch = changes.formData !== undefined && changes.formData !== null;

    if (!hasPositionPatch && !hasFormPatch) return false;

    if (hasPositionPatch) {
      const updated = this.dataService.annotationBlockStore.patchBlockPosition(
        id,
        positionPatch,
        options
      );
      if (!updated) return false;
    }
    if (hasFormPatch) {
      this.dataService.annotationBlockStore.patchBlockFormData(id, changes.formData);
    }

    this.dataService.notifyListeners();
    return true;
  }

  moveAnnotations(changes, options) {
    if (!this.dataService.annotationSheet) {
      return { success: false, reason: 'sheet-not-initialized' };
    }

    const result = this.dataService.annotationBlockStore.moveBlocks(changes, options);
    if (!result.success) return result;
    this.dataService.notifyListeners();
    return result;
  }

  /**
   * Delete an annotation by session ID. Accept an annotation object temporarily
   * for callers that have not yet migrated, but use only its ID.
   */
  deleteAnnotation(idOrAnnotation) {
    if (!this.dataService.annotationSheet) return false;

    const id = typeof idOrAnnotation === 'string' ? idOrAnnotation : idOrAnnotation?.id;
    const deleted = id ? this.dataService.annotationBlockStore.deleteBlock(id) : false;
    if (!deleted) return false;

    this.dataService.notifyListeners();
    return true;
  }

  deleteAnnotations(ids) {
    if (!this.dataService.annotationSheet) {
      return { success: false, reason: 'sheet-not-initialized' };
    }

    const result = this.dataService.annotationBlockStore.deleteBlocks(ids);
    if (!result.success) return result;
    this.dataService.notifyListeners();
    return result;
  }
}
